import { DOMUtils } from '../../../../utils/dom'
import { SimpleEvents } from '../../../../utils/events'
import { IdentityEvent, IdentityEvents } from '../../../common/events/identity'
import { IProfile } from '../../../common/profile'
import { IIdentityProviderConfig, IIdentityServiceAdapter, SSOScreen } from '../adapter'

interface IWindow {
    janrain: any
    janrainCaptureWidgetOnLoad: () => void
    janrainWidgetOnload: () => void
}

declare let window: IWindow

export interface IJanrainConfig extends IIdentityProviderConfig {
    account: string
    application: string
    client: string
    logout?: string
    redirect?: string
    token?: string
    xd?: string
}

export class JanrainAdapter extends SimpleEvents implements IIdentityServiceAdapter<IJanrainConfig> {
    private eventHandlers: { [key: string]: (...args: any[]) => void } = {
        onCaptureLoginSuccess: this.onChange,
        onCaptureSessionEnded: this.onChange,
        onCaptureSessionFound: this.onChange,
        onCaptureSessionNotFound: this.onChange
    }
    private jsUrl = '//widgets-cdn.rpxnow.com/manifest/capture:login'
    private preConfigured = false

    // default configuration - ideally no need to allow page config for these
    private settings = {
        capture: {
            federate: true,
            federateEnableSafari: true,
            flowName: 'standard',
            responseType: 'token',
            setProfileCookie: true, // saves a call, apparently
            setProfileData: true
        },
        providers: [
            'facebook',
            'googleplus',
            'linkedin',
            'openid',
            'twitter',
            'yahoo'],
        tokenAction: 'event'
    }

    /**
     * Begins SSO session.
     */
    public enable() {
        // register event handlers we need to be aware of
        Object.keys(this.eventHandlers).forEach((key) =>
            window.janrain.events[key].addHandler(this.eventHandlers[key].bind(this)))

        // DEV - register all available handlers
        // Object.keys(window.janrain.events).forEach(k => {
        //     if (window.janrain.events.hasOwnProperty(k) && window.janrain.events[k].addHandler) {
        //         window.janrain.events[k].addHandler((...args: any[]) =>
        //             console.log(`${k} =`, ...args))
        //     }
        // })

        if (!this.preConfigured) {
            window.janrain.capture.ui.start()
        }
    }

    /**
     * Adds email/name/uuid to profile from Janrain.
     *
     * @returns {Promise<IProfile>}
     */
    public fetch(): IProfile {
        const profileData = {
            email: undefined,
            name: undefined,
            uuid: undefined
        } as IProfile

        try {
            const get = window.janrain.capture.ui.getProfileCookieData
            if (window.janrain.capture.ui.hasActiveSession()) {
                profileData.email = get('email')
                profileData.name = get('displayName')
                profileData.uuid = get('uuid')
            }
        } catch (e) {
            console.log('Problem retrieving profile data from Janrain cookie:', e)
        }

        return profileData
    }

    /**
     * Sets up global object with settings, loads JS.
     *
     * @param conf
     * @returns {IIdentityServiceAdapter} for chaining
     */
    public initialize(conf: IJanrainConfig) {

        /**
         * This may or may not be required in the future, but for sake of migrations,
         * we're not initializing when the global object is already present
         * (meaning the backend has presumably configured it loaded the js).
         * We still add the init handler, but don't perform configuration/initialization.
         */
        this.preConfigured = !!window.janrain
        if (this.preConfigured) {
            this.registerInitHandler()
            return this
        }

        // setup global object with settings
        window.janrain = window.janrain || { settings: {} }
        window.janrain = {
            ...window.janrain,
            ...{
                settings: {
                    ...this.settings,
                    capture: {
                        ...this.settings.capture,
                        appId: conf.account,
                        captureServer: `https://${conf.application}.janraincapture.com`,
                        clientId: conf.client,
                        federateLogoutUri: conf.logout,
                        // NOTE: this expects app name to be same for capture+sso, however they can differ
                        // (as does 'Postmedia dev (iframe)'). Would prefer we/Janrain update so they're the same.
                        federateServer: `https://${conf.application}.janrainsso.com`,
                        federateXdReceiver: conf.xd,
                        redirectUri: conf.redirect
                    },
                    tokenUrl: conf.token
                }
            },
            ready: true
        }

        // enable debugging outside prod
        // if (__NON_PROD) {
            // this causes repeated share widget load warnings that are apparently okay,
            // but clutter up the console. Enable as needed for local dev only:
        //     window.janrain.settings.debug = { enabled: true }
        // }

        // register `ready` handlers
        this.registerInitHandler()

        // load JS
        DOMUtils.loadScript(this.jsUrl)

        // make chainable
        return this
    }

    /**
     * Handles identity changes (sign in/out).
     */
    public onChange() {
        window.janrain.capture.ui.modal.close()
        this.dispatchEvent(IdentityEvent(IdentityEvents.IDENTITY_CHANGE, this.fetch()))
    }

    /**
     * Registers handlers for init events. Separate method for overriding via traits.
     *
     * NOTE: this preserves existing handler for sake of migration, but as 'existing' suggests,
     * it must already be set. Setting the handler after here will prevent this from registering
     * required listeners.
     */
    public registerInitHandler() {
        const existingHandler = window.janrainCaptureWidgetOnLoad
        window.janrainCaptureWidgetOnLoad = () => {
            this.enable()
            if (existingHandler) {
                existingHandler()
            }
        }
        // dangerous now that accommodating pre-configured
        // window.janrainWidgetOnload = () => undefined // this just to suppress a warning
    }

    /**
     * Opens sign-in modal.
     */
    public signIn() {
        window.janrain.capture.ui.renderScreen(SSOScreen.SIGN_IN)
    }

    /**
     * Ends SSO session.
     */
    public signOut() {
        window.janrain.capture.ui.endCaptureSession()
    }
}
